import { ScrollTop } from "@/components/commons/ScrollTop";
import { AppDrawer } from "@/components/layout/elements/AppDrawer";
import { AppHeader } from "@/components/layout/elements/AppHeader";
import { Chat } from "@/features/chat/components/Chat";
import { ChatProvider } from "@/features/chat/providers/ChatProvider";
import { SocketProvider } from "@/features/chat/providers/SocketProvider";
import { withAuth } from "@/hoc/withAuth";
import { useVisible } from "@/hooks/useVisible";
import { InitSetupProvider } from "@/providers/InitSetupProvider";
import { useMaintenance } from "@/providers/MaintenanceProvider";
import { background } from "@/theme/colors";
import { spacing } from "@/theme/variables";
import { FCC } from "@/types/common";
import { MAINTENANCE_MODE } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { Box, Stack, styled } from "@mui/material";
import { useMemo } from "react";
import { Outlet } from "react-router-dom";

export const CompanyLayout: FCC = ({ children }) => {
  const showDrawer = useVisible();
  const { data } = useMaintenance();

  const isMaintenance = useMemo(() => {
    if (!data?.maintenanceMode || data.maintenanceMode === MAINTENANCE_MODE.SYSTEM_NORMAL_OPERATION) {
      return false;
    }

    const now = dayjs();
    const start = data.modeStartDateTime ? dayjs(data.modeStartDateTime) : null;
    const end = data.modeEndDateTime ? dayjs(data.modeEndDateTime) : null;

    return (!start || now.isSameOrAfter(start)) && (!end || now.isSameOrBefore(end));
  }, [data]);

  return (
    <InitSetupProvider>
      <ScrollTop />
      <AppHeader handleOpenDrawer={showDrawer.open} />
      <StackWrapper direction="row">
        <AppDrawer isOpenDrawer={showDrawer.visible} toggleDrawer={showDrawer.toggle} />
        <ChildrenWrapper onClick={showDrawer.close}>
          <Outlet />
          {children}
        </ChildrenWrapper>
      </StackWrapper>
      {!isMaintenance && (
        <SocketProvider>
          <ChatProvider>
            <Chat />
          </ChatProvider>
        </SocketProvider>
      )}
    </InitSetupProvider>
  );
};

export const CompanyLayoutWithAuth = withAuth(CompanyLayout);

const ChildrenWrapper = styled(Box)`
  padding: ${spacing.md};
  padding-top: ${spacing.lg};
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  max-width: 1200px;
  margin: 0 auto;
`;

const StackWrapper = styled(Stack)`
  min-height: calc(100vh - 57px);
  background-color: ${background.primary};
`;
